import { useState } from "react";
import { ListPlus, Plus, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

const VISIBLE_COUNT = 5;

// Zone d'ajout rapide à une liste, partagée par toutes les vues : les
// premières listes sont des boutons directement cliquables, le reste passe
// dans un menu "+N", et "Nouvelle liste" est toujours accessible.
// Chaque clic ajoute (une carte, ou la sélection courante) : le bouton
// devient vert et affiche un compteur ×N pour montrer combien de fois on a
// ajouté — on peut ainsi mettre plusieurs exemplaires d'une carte.
export const AddToListButtons = ({ lists, disabled = false, onAddToList, onCreateList }) => {
  const [counts, setCounts] = useState({}); // { listId: nb de clics }
  // `window.prompt` ne fonctionne pas dans la WebView Android de Capacitor
  // (dialogue natif non supporté) : sans liste existante, "Nouvelle liste"
  // était donc un bouton mort sur mobile. Remplacé par un champ inline.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const visible = lists.slice(0, VISIBLE_COUNT);
  const overflow = lists.slice(VISIBLE_COUNT);

  const bump = (listId) => setCounts(prev => ({ ...prev, [listId]: (prev[listId] || 0) + 1 }));

  const addTo = async (listId) => {
    await onAddToList(listId);
    bump(listId);
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) { setCreating(false); return; }
    const created = await onCreateList(name);
    if (created?.id) {
      await onAddToList(created.id);
      bump(created.id);
    }
    setNewName("");
    setCreating(false);
  };

  const listButton = (l) => {
    const n = counts[l.id] || 0;
    const added = n > 0;
    return (
      <Button key={l.id} size="sm" variant="outline" disabled={disabled}
        className={`disabled:opacity-40 ${added
          ? "bg-emerald-600 border-emerald-500 text-white hover:bg-emerald-500"
          : "border-zinc-700 text-white"}`}
        onClick={() => addTo(l.id)}>
        {added && <Check className="w-3.5 h-3.5 mr-1" />}
        {l.name}
        {n > 1 && <span className="ml-1.5 font-bold">×{n}</span>}
      </Button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-zinc-500 flex items-center gap-1">
        <ListPlus className="w-3.5 h-3.5" />Ajouter à :
      </span>
      {visible.map(listButton)}
      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={disabled}
              className="border-zinc-700 text-white disabled:opacity-40">
              +{overflow.length}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
            {overflow.map(l => (
              <DropdownMenuItem key={l.id} onClick={() => addTo(l.id)}
                className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                {l.name}{(counts[l.id] || 0) > 0 && <span className="ml-2 text-emerald-400">×{counts[l.id]}</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {creating ? (
        <div className="flex items-center gap-1.5">
          <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") createAndAdd();
              if (e.key === "Escape") { setCreating(false); setNewName(""); }
            }}
            placeholder="Nom de la liste" className="h-8 w-40 bg-zinc-800 border-zinc-700 text-sm" />
          <Button size="sm" disabled={disabled || !newName.trim()}
            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
            onClick={createAndAdd}>
            <Check className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" disabled={disabled}
          className="text-zinc-400 hover:text-white disabled:opacity-40"
          onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-1" />Nouvelle liste
        </Button>
      )}
    </div>
  );
};
